/**
 * WhatsChangedStrip — slim "since your last visit" line at the very
 * top of the dashboard. Renders a friendly headline composed
 * server-side (no LLM) plus tiny color-coded delta chips for PE,
 * streak, and the top KPI movement. Hidden on first visit, hidden
 * if the last visit was less than an hour ago, and hidden on error.
 *
 * `compact` prop trims padding/font for the redesigned 4-zone
 * dashboard layout where this sits as a slim status strip rather
 * than a CTA card.
 */
import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { pieApi } from '../pieApi';
import logger from '../../../lib/logger';

export default function WhatsChangedStrip({ compact = false }) {
  const [data, setData] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const firedRef = React.useRef(false);

  useEffect(() => {
    // Fire exactly once even under React StrictMode dev double-mount.
    // Don't guard setData with a `mounted` flag — under StrictMode the
    // first effect's cleanup runs BEFORE the API resolves, which would
    // otherwise drop the response on the floor and leave data null.
    if (firedRef.current) return undefined;
    firedRef.current = true;
    pieApi
      .whatsChanged()
      .then((d) => {
        setData(d);
        // Advance the visit baseline 5 seconds AFTER the strip renders.
        // Delay protects against transient page-loads (StrictMode
        // double-mount, fast-back-and-forth) overwriting a real prior.
        if (d && d.current) {
          setTimeout(() => {
            pieApi.whatsChangedAck(d.current).catch(() => { /* ignore */ });
          }, 5000);
        }
      })
      .catch((e) => logger.warn('whatsChanged failed', e));
    return undefined;
  }, []);

  if (!data || dismissed) return null;
  if (data.suppressed) return null;

  const isFirst = !!data.first_visit;
  const peClass = data.pe_delta_pp > 0
    ? 'pie-whats-changed-chip-up'
    : data.pe_delta_pp < 0 ? 'pie-whats-changed-chip-down' : 'pie-whats-changed-chip-flat';

  return (
    <div
      className={compact ? 'pie-whats-changed pie-whats-changed-compact' : 'pie-whats-changed'}
      data-testid="pie-whats-changed"
    >
      <span className="pie-whats-changed-eyebrow pie-mono">
        {isFirst ? 'Welcome' : `Since ${data.since_human || 'your last visit'}`}
      </span>
      <span className="pie-whats-changed-text">{data.headline}</span>
      {!isFirst && (
        <span className="pie-whats-changed-chips">
          <span className={`pie-whats-changed-chip ${peClass}`} data-testid="pie-whats-changed-chip-pe">
            PE {data.pe_delta_pp > 0 ? '+' : ''}{data.pe_delta_pp}pt
          </span>
          {data.streak_delta_weeks !== 0 && (
            <span
              className={`pie-whats-changed-chip ${data.streak_delta_weeks > 0 ? 'pie-whats-changed-chip-up' : 'pie-whats-changed-chip-down'}`}
              data-testid="pie-whats-changed-chip-streak"
            >
              Streak {data.streak_delta_weeks > 0 ? '+' : ''}{data.streak_delta_weeks}w
            </span>
          )}
          {data.top_kpi_change && data.top_kpi_change.delta_pct !== 0 && (
            <span
              className={`pie-whats-changed-chip ${data.top_kpi_change.delta_pct > 0 ? 'pie-whats-changed-chip-up' : 'pie-whats-changed-chip-down'}`}
              data-testid="pie-whats-changed-chip-kpi"
              title={data.top_kpi_change.name}
            >
              {data.top_kpi_change.delta_pct > 0 ? '+' : ''}{data.top_kpi_change.delta_pct}pt
            </span>
          )}
        </span>
      )}
      <button
        type="button"
        className="pie-whats-changed-dismiss"
        onClick={() => setDismissed(true)}
        data-testid="pie-whats-changed-dismiss"
        aria-label="Dismiss"
      >
        <X size={12} aria-hidden />
      </button>
    </div>
  );
}
